import { AccessTime, Close } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type Dispatch, type FC, type SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { NEUTRAL } from "../../../theme/tokens";
import type { NurseFullRole } from "../../../utils/models";
import { inverseRolesMap } from "../../../utils/utilFunctions";
import { EditTimePopover } from "../../EditTimePopover";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

export type RoleSource = "nurse" | "role";

type Props = {
	selectedRoleForShift: NurseFullRole | undefined;
	shiftStart: DateTime;
	shiftEnd: DateTime;
	setShiftStart: ((start: DateTime) => void) | Dispatch<SetStateAction<DateTime>>;
	setShiftEnd: ((end: DateTime) => void) | Dispatch<SetStateAction<DateTime>>;
	roleSource: RoleSource;
	selectedRoleGroups?: string[];
	enabled?: boolean;
	/** Tooltip shown while disabled. Defaults to "Make sure to select a {roleSource} first" — the
	 * original disabled reason this component was built for. Callers that disable it via `enabled` for
	 * a different reason (e.g. no date picked yet) must override this, or the tooltip will name the
	 * wrong missing field. */
	disabledTooltip?: string;
	jobId?: string;
	hideLabel?: boolean;
	/** Optional — omit for callers where the hours are a required field (e.g. `AddDayOffDialog`, which
	 * always needs a concrete value). When provided, renders a small clear icon inside the box, before
	 * the clock icon; clicking it calls `onClear` instead of opening the picker. */
	onClear?: () => void;
	/** The time editor's look (`EditTimePopover`'s `appearance`): `touch` for a picker inside a phone sheet. Absent = today's editor. */
	editorAppearance?: "default" | "touch";
	/** Optional exclusive elapsed-duration limit for write surfaces with a stricter invariant. */
	maximumDurationHours?: number;
};

export const ShiftHoursDisplayAndPicker: FC<Props> = ({
	selectedRoleForShift,
	shiftEnd,
	setShiftEnd,
	shiftStart,
	setShiftStart,
	roleSource,
	selectedRoleGroups,
	enabled,
	disabledTooltip,
	jobId,
	hideLabel,
	onClear,
	editorAppearance,
	maximumDurationHours,
}) => {
	const [anchorRef, setAnchorRef] = useState<HTMLElement | null>(null);
	const [isEditingTime, setIsEditingTime] = useState(false);
	const { timezone } = useDepartmentFlags();
	const { t } = useTranslation();
	const isEnabled = enabled ?? !!selectedRoleForShift;
	return (
		<Box
			sx={{
				width: "100%",
				opacity: isEnabled ? 1 : 0.5,
			}}
		>
			{!hideLabel && (
				<Typography
					sx={(theme) => ({
						color: theme.palette.primary.main,
						mb: 0.5,
						fontSize: "13px",
					})}
				>
					Choose Hours
				</Typography>
			)}
			<InHouseTooltip title={disabledTooltip ?? `Make sure to select a ${roleSource} first`} disableHoverListener={isEnabled}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						border: `1px solid ${NEUTRAL[300]}`,
						borderRadius: 1,
						p: 1,
						height: "40px",
						justifyContent: "space-between",
						cursor: isEnabled ? "pointer" : "not-allowed",
						":hover": {
							bgcolor: isEnabled ? NEUTRAL[100] : "inherit",
						},
					}}
					onClick={(e) => {
						if (isEnabled) {
							setAnchorRef(e.currentTarget);
							setIsEditingTime(true);
						}
					}}
				>
					<Typography>
						{shiftStart.setZone(timezone).toFormat("h:mm a")} - {shiftEnd.setZone(timezone).toFormat("h:mm a")}
					</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						{onClear && (
							<IconButton
								size="small"
								aria-label={t("shift_hours_picker.clear")}
								onClick={(e) => {
									e.stopPropagation();
									onClear();
								}}
								sx={{ p: 0.25 }}
							>
								<Close sx={{ fontSize: 16 }} />
							</IconButton>
						)}
						<AccessTime sx={(theme) => ({ color: theme.palette.primary.main })} />
					</Box>
				</Box>
			</InHouseTooltip>
			{isEditingTime && isEnabled && anchorRef && (
				<EditTimePopover
					roleGroups={selectedRoleGroups}
					jobId={jobId}
					role={selectedRoleGroups || !selectedRoleForShift ? undefined : inverseRolesMap[selectedRoleForShift]}
					startTime={shiftStart}
					endTime={shiftEnd}
					onTimeChange={({ newStartTime, newEndTime }) => {
						setShiftStart(newStartTime);
						setShiftEnd(newEndTime);
					}}
					onClose={() => setIsEditingTime(false)}
					anchorEl={anchorRef}
					isActual={false}
					appearance={editorAppearance}
					maximumDurationHours={maximumDurationHours}
				/>
			)}
		</Box>
	);
};
